import { WebStorageStateStore } from 'oidc-client-ts';
import { useAuth, type AuthProviderProps } from 'react-oidc-context';

const authority = import.meta.env.VITE_COGNITO_AUTHORITY;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;

if (!authority || !clientId || !cognitoDomain) {
  // Surfaces clearly in dev if env vars are missing — don't ship a half-configured app.
  throw new Error(
    'Missing Cognito env vars. Copy .env.example to .env.local and fill in values from SAM stack outputs.',
  );
}

export const oidcConfig: AuthProviderProps = {
  authority,
  client_id: clientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope: 'openid email profile',
  // localStorage, not sessionStorage: sessionStorage is per tab, so a link
  // opened in a new tab started with no user and went through sign-in again.
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Strip ?code=... from the URL after the callback so it doesn't sit in history.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

/** Where to land after sign-in: the page that sent us to sign in. */
export type SigninState = { returnTo?: string };

export function currentPath(): string {
  const { pathname, search, hash } = window.location;
  return pathname === '/auth/callback' ? '/' : pathname + search + hash;
}

/** Only same-origin paths — never let the state carry us off-site. */
export function safeReturnTo(state: unknown): string {
  const to = (state as SigninState | undefined)?.returnTo;
  return typeof to === 'string' && to.startsWith('/') && !to.startsWith('//') ? to : '/';
}

/**
 * Cognito Hosted-UI logout. The OIDC end-session endpoint isn't quite right for
 * Cognito — they use a non-standard /logout URL — so we hit it directly. Local
 * OIDC user state (sessionStorage) must be cleared *before* we redirect, or
 * react-oidc-context will rehydrate the old user when Cognito bounces us back.
 *
 * IMPORTANT: logout_uri must match a value in the User Pool Client's LogoutURLs
 * list EXACTLY, including any trailing slash. The CFN template registers
 * `https://${SpaDistribution.DomainName}/`, so we keep the trailing slash here
 * too. window.location.origin strips trailing slashes, so we add one back.
 */
let signingOut = false;

/**
 * True once sign-out has begun. Removing the user makes the app look signed
 * out, and App would answer that with a sign-in redirect that overtakes the
 * trip to Cognito's /logout — landing the user straight back in, still signed in.
 */
export function isSigningOut(): boolean {
  return signingOut;
}

export function useCognitoSignOut(): () => Promise<void> {
  const auth = useAuth();
  return async () => {
    signingOut = true;
    await auth.removeUser();
    const url = new URL(`${cognitoDomain}/logout`);
    url.searchParams.set('client_id', clientId);
    url.searchParams.set('logout_uri', `${window.location.origin}/`);
    window.location.href = url.toString();
  };
}
